import { useEffect, useState, useCallback } from "react";
import { User, IApiService } from "./apiService";
import "./App.css";

interface Props {
    apiService: IApiService;
    user: { name: string; email: string; token: string };
    onBack: () => void;
}

interface UserFormData {
    id?: string;
    email: string;
    name: string;
    is_admin: boolean;
}

interface FormErrors {
    email?: string;
    name?: string;
}

export default function UserAdminPage({ apiService, user, onBack }: Props) {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<"create" | "edit">("create");
    const [formData, setFormData] = useState<UserFormData>({
        email: "",
        name: "",
        is_admin: false,
    });
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [submitting, setSubmitting] = useState(false);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiService.getUsers(user.token);
            switch (res.type) {
                case "expired":
                    setError("Session expired, please log in again.");
                    break;
                case "ok":
                    setUsers(res.data || []);
                    break;
                case "error":
                    setError(`Failed to fetch users`);
            }
        } catch (err: any) {
            setError(err.message);
        }
        setLoading(false);
    }, [apiService, user.token]);

    useEffect(() => {
        void fetchUsers();
    }, [fetchUsers]);

    function openCreateModal() {
        setFormData({
            email: "",
            name: "",
            is_admin: false,
        });
        setFormErrors({});
        setModalMode("create");
        setShowModal(true);
    }

    function openEditModal(userToEdit: User) {
        setFormData({
            id: userToEdit.id,
            email: userToEdit.email,
            name: userToEdit.name,
            is_admin: userToEdit.is_admin,
        });
        setFormErrors({});
        setModalMode("edit");
        setShowModal(true);
    }

    function closeModal() {
        setShowModal(false);
        setFormData({
            email: "",
            name: "",
            is_admin: false,
        });
        setFormErrors({});
    }

    function validateForm(): boolean {
        const errors: FormErrors = {};

        if (!formData.name.trim()) {
            errors.name = "Name is required";
        }

        if (!formData.email.trim()) {
            errors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = "Please enter a valid email address";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    async function checkEmailExists(email: string, excludeId?: string): Promise<boolean> {
        try {
            const res = await apiService.getUserByEmail(user.token, email);
            if (res.type === "ok") {
                return res.data.id !== excludeId;
            }
            return false;
        } catch {
            return false;
        }
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        // Check if email already exists
        const emailExists = await checkEmailExists(formData.email, formData.id);
        if (emailExists) {
            setFormErrors({ email: "This email is already in use" });
            setSubmitting(false);
            return;
        }

        function saveUser() {
            switch (modalMode) {
                case "create":
                    return apiService.createUser(user.token, formData.email, formData.name, formData.is_admin);
                case "edit":
                    return apiService.updateUser(user.token, {
                        id: formData.id ?? "",
                        email: formData.email,
                        name: formData.name,
                        is_admin: formData.is_admin
                    });
            }
        }

        try {
            const res = await saveUser();
            closeModal();
            switch (res.type) {
                case "expired":
                    setError("Session expired, please log in again.");
                    break;
                case "error":
                    setError(`Failed to ${modalMode} user`);
                    break;
                case "ok":
                    await fetchUsers();
                    break;
            }
        } catch (err: any) {
            setError(err.message);
        }

        setSubmitting(false);
    }

    async function handleDelete(userToDelete: User) {
        // Use window.confirm to avoid ESLint error
        if (!window.confirm(`Are you sure you want to delete user "${userToDelete.name}" (${userToDelete.email})?`)) {
            return;
        }

        try {
            const res = await apiService.deleteUser(user.token, userToDelete.id);
            switch (res.type) {
                case "expired":
                    setError("Session expired, please log in again.");
                    break;
                case "ok":
                    await fetchUsers(); // Refresh the list
                    break;
                case "error":
                    setError(`Failed to delete user`);
                    break;
            }
        } catch (err: any) {
            setError(err.message);
        }
    }

    return (
        <>
            <div
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1em" }}
            >
                <button className="menu-link" onClick={onBack} type="button">
                    ← Back
                </button>
                <h2 style={{ margin: 0 }}>User Administration</h2>
                <button className="menu-link" onClick={openCreateModal} style={{ padding: "0.5em 1.2em" }}>
                    Add User
                </button>
            </div>

            {loading && <div style={{ margin: "1.5em 0" }}>Loading users...</div>}
            {error && <div style={{ color: "#d22", margin: "1.5em 0" }}>{error}</div>}

            {!loading && users.length > 0 && (
                <div style={{ width: "100%", overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "1em" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid #e4e8f0" }}>
                                <th align="left" style={{ padding: "0.5em" }}>
                                    ID
                                </th>
                                <th align="left" style={{ padding: "0.5em" }}>
                                    Name
                                </th>
                                <th align="left" style={{ padding: "0.5em" }}>
                                    Email
                                </th>
                                <th align="center" style={{ padding: "0.5em" }}>
                                    Admin
                                </th>
                                <th align="center" style={{ padding: "0.5em" }}>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((userRow) => (
                                <tr key={userRow.id} style={{ borderBottom: "1px solid #f0f1f4" }}>
                                    <td style={{ padding: "0.5em" }}>{userRow.id}</td>
                                    <td style={{ padding: "0.5em" }}>{userRow.name}</td>
                                    <td style={{ padding: "0.5em" }}>{userRow.email}</td>
                                    <td align="center" style={{ padding: "0.5em" }}>
                                        {userRow.is_admin ? "✓" : ""}
                                    </td>
                                    <td align="center" style={{ padding: "0.5em" }}>
                                        <button
                                            onClick={() => openEditModal(userRow)}
                                            style={{
                                                marginRight: "0.5em",
                                                padding: "0.25em 0.5em",
                                                fontSize: "0.8em",
                                                backgroundColor: "#007acc",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "4px",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(userRow)}
                                            style={{
                                                padding: "0.25em 0.5em",
                                                fontSize: "0.8em",
                                                backgroundColor: "#d22",
                                                color: "white",
                                                border: "none",
                                                borderRadius: "4px",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && users.length === 0 && !error && <div style={{ margin: "1.5em 0" }}>No users found.</div>}

            {/* Modal */}
            {showModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            padding: "2em",
                            borderRadius: "8px",
                            minWidth: "400px",
                            maxWidth: "90%",
                        }}
                    >
                        <h3 style={{ marginTop: 0 }}>{modalMode === "create" ? "Add New User" : "Edit User"}</h3>

                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: "1em" }}>
                                <label style={{ display: "block", marginBottom: "0.5em" }}>Name *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    style={{
                                        width: "100%",
                                        padding: "0.5em",
                                        fontSize: "1rem",
                                        border: formErrors.name ? "1px solid #d22" : "1px solid #ccc",
                                        borderRadius: "4px",
                                    }}
                                />
                                {formErrors.name && (
                                    <div style={{ color: "#d22", fontSize: "0.8em", marginTop: "0.25em" }}>
                                        {formErrors.name}
                                    </div>
                                )}
                            </div>

                            <div style={{ marginBottom: "1em" }}>
                                <label style={{ display: "block", marginBottom: "0.5em" }}>Email *</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    style={{
                                        width: "100%",
                                        padding: "0.5em",
                                        fontSize: "1rem",
                                        border: formErrors.email ? "1px solid #d22" : "1px solid #ccc",
                                        borderRadius: "4px",
                                    }}
                                />
                                {formErrors.email && (
                                    <div style={{ color: "#d22", fontSize: "0.8em", marginTop: "0.25em" }}>
                                        {formErrors.email}
                                    </div>
                                )}
                            </div>

                            <div style={{ marginBottom: "1.5em" }}>
                                <label style={{ display: "flex", alignItems: "center" }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.is_admin}
                                        onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                                        style={{ marginRight: "0.5em" }}
                                    />
                                    Administrator
                                </label>
                            </div>

                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5em" }}>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    disabled={submitting}
                                    style={{
                                        padding: "0.5em 1em",
                                        backgroundColor: "#ccc",
                                        color: "black",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: submitting ? "not-allowed" : "pointer",
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    style={{
                                        padding: "0.5em 1em",
                                        backgroundColor: "#007acc",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: submitting ? "not-allowed" : "pointer",
                                    }}
                                >
                                    {submitting ? "Saving..." : modalMode === "create" ? "Create" : "Update"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
